import { useState, useRef } from "react";
import {
    Grid, Card, Icon, Tooltip, CircularProgress, Chip, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { postUpload, postUploadFile } from "utils/WebService";
import { errorAlert, successAlert } from "utils/Functions";
import { useMaterialUIController } from "context";
import { ESCURO } from "assets/coresTema";

export default function ComparadorRecortes() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const [file1, setFile1] = useState(null);
    const [file2, setFile2] = useState(null);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    const [result, setResult] = useState(null);
    const [filterStatus, setFilterStatus] = useState("MISSING"); // "MISSING" | "ALL" | "FOUND"
    const [searchTerm, setSearchTerm] = useState("");

    const file1InputRef = useRef(null);
    const file2InputRef = useRef(null);

    const handleProcessComparison = () => {
        if (!file1) {
            errorAlert("Por favor selecione o Arquivo 1 (Base / Origem).");
            return;
        }
        if (!file2) {
            errorAlert("Por favor selecione o Arquivo 2 (Comparação / Destino).");
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append("file1", file1);
        formData.append("file2", file2);

        postUpload("/robo/comparador-recortes/processar", formData)
            .then(res => {
                setLoading(false);
                setResult(res);
                if (res?.missingCount === 0) {
                    successAlert("Todos os processos do Arquivo 2 constam no Arquivo 1!");
                } else {
                    successAlert(`Comparação concluída! Encontrados ${res?.missingCount || 0} processos do Arquivo 2 que NÃO constam no Arquivo 1.`);
                }
            })
            .catch(err => {
                setLoading(false);
                errorAlert(err?.message || "Erro ao processar e comparar arquivos.");
            });
    };

    const handleExportMissing = () => {
        if (!file1 || !file2) {
            errorAlert("Selecione ambos os arquivos para exportar.");
            return;
        }

        setExporting(true);
        const formData = new FormData();
        formData.append("file1", file1);
        formData.append("file2", file2);
        let dynamicFilename = undefined;
        if (result && result.allRecords && result.allRecords.length > 0) {
            const itemWithData = result.allRecords.find(r => r.data && r.data.trim());
            if (itemWithData && itemWithData.data) {
                const match = itemWithData.data.trim().match(/\b(\d{2})[/.-](\d{2})[/.-](\d{4})\b/);
                if (match) {
                    dynamicFilename = `RECORTES ${match[1]} ${match[2]} ${match[3]} ADENDO.xlsx`;
                }
            }
        }

        postUploadFile("/robo/comparador-recortes/exportar", formData, dynamicFilename)
            .then(() => {
                setExporting(false);
                successAlert("Planilha baixada com sucesso (.xlsx)!");
            })
            .catch(err => {
                setExporting(false);
                errorAlert(err?.message || "Erro ao gerar arquivo para download.");
            });
    };


    const displayedRecords = () => {
        if (!result) return [];
        let list = filterStatus === "MISSING"
            ? (result.missingRecords || [])
            : (result.allRecords || []);

        if (filterStatus === "FOUND") {
            list = (result.allRecords || []).filter(r => r.status === "LOCALIZADO");
        }

        if (searchTerm.trim()) {
            const q = searchTerm.toLowerCase();
            list = list.filter(r => {
                const proc = (r.processNumber || "").toLowerCase();
                const raw = (r.rawIntimacaoText || "").toLowerCase();
                return proc.includes(q) || raw.includes(q);
            });
        }

        return list;
    };

    const textPrimary = darkMode ? "#ffffff" : "#344767";
    const textSecondary = darkMode ? "rgba(255,255,255,0.7)" : "#6c757d";

    return (
        <MDBox pt={3} pb={3}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card
                        variant="outlined"
                        sx={{
                            p: 2,
                            background: darkMode ? ESCURO.cartao : "#ffffff",
                            borderColor: darkMode ? ESCURO.borda : "#e0e0e0"
                        }}
                    >
                        <MDBox mb={2.5} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                            <MDBox display="flex" alignItems="center" gap={1.5}>
                                <MDBox p={1} borderRadius="10px" sx={{ backgroundColor: "rgba(26, 115, 232, 0.15)", color: "#1A73E8", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Icon fontSize="medium">find_replace</Icon>
                                </MDBox>
                                <MDTypography variant="button" sx={{ color: textSecondary, fontWeight: 500 }}>
                                    🤖 Compare duas bases e identifique processos não localizados
                                </MDTypography>
                            </MDBox>

                            {result && (
                                <MDButton
                                    color="success"
                                    variant="contained"
                                    disabled={exporting}
                                    onClick={handleExportMissing}
                                    startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <Icon>file_download</Icon>}
                                >
                                    {exporting ? "Gerando Excel..." : "Baixar Planilha de Não Localizados (.xlsx)"}
                                </MDButton>
                            )}
                        </MDBox>

                        {/* UPLOAD SECTION */}
                        <Grid container spacing={3} mb={3}>
                            <Grid item xs={12} md={6}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        borderStyle: "dashed",
                                        borderWidth: "2px",
                                        borderColor: file1 ? "#4CAF50" : (darkMode ? ESCURO.borda : "#ccc"),
                                        backgroundColor: darkMode ? ESCURO.painel : "#f8f9fa",
                                        textAlign: "center",
                                        cursor: "pointer"
                                    }}
                                    onClick={() => file1InputRef.current?.click()}
                                >
                                    <input
                                        type="file"
                                        ref={file1InputRef}
                                        style={{ display: "none" }}
                                        accept=".xlsx, .xls, .csv, .docx, .doc"
                                        onChange={(e) => setFile1(e.target.files[0] || null)}
                                    />
                                    <Icon color={file1 ? "success" : "info"} sx={{ fontSize: 40, mb: 1 }}>
                                        {file1 ? "task_alt" : "description"}
                                    </Icon>
                                    <MDTypography variant="h6" sx={{ color: textPrimary }} fontWeight="bold">
                                        Arquivo 1: Base / Origem
                                    </MDTypography>
                                    <MDTypography variant="caption" sx={{ color: textSecondary }} display="block" mb={1}>
                                        {file1 ? file1.name : "Clique para selecionar Excel (.xlsx) ou Word (.docx)"}
                                    </MDTypography>
                                    <MDButton size="small" variant="outlined" color={file1 ? "success" : "info"}>
                                        {file1 ? "Alterar Arquivo 1" : "Selecionar Arquivo 1"}
                                    </MDButton>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        borderStyle: "dashed",
                                        borderWidth: "2px",
                                        borderColor: file2 ? "#4CAF50" : (darkMode ? ESCURO.borda : "#ccc"),
                                        backgroundColor: darkMode ? ESCURO.painel : "#f8f9fa",
                                        textAlign: "center",
                                        cursor: "pointer"
                                    }}
                                    onClick={() => file2InputRef.current?.click()}
                                >
                                    <input
                                        type="file"
                                        ref={file2InputRef}
                                        style={{ display: "none" }}
                                        accept=".xlsx, .xls, .csv, .docx, .doc"
                                        onChange={(e) => setFile2(e.target.files[0] || null)}
                                    />
                                    <Icon color={file2 ? "success" : "info"} sx={{ fontSize: 40, mb: 1 }}>
                                        {file2 ? "task_alt" : "analytics"}
                                    </Icon>
                                    <MDTypography variant="h6" sx={{ color: textPrimary }} fontWeight="bold">
                                        Arquivo 2: Recortes / Destino
                                    </MDTypography>
                                    <MDTypography variant="caption" sx={{ color: textSecondary }} display="block" mb={1}>
                                        {file2 ? file2.name : "Clique para selecionar Excel (.xlsx) ou Word (.docx)"}
                                    </MDTypography>
                                    <MDButton size="small" variant="outlined" color={file2 ? "success" : "info"}>
                                        {file2 ? "Alterar Arquivo 2" : "Selecionar Arquivo 2"}
                                    </MDButton>
                                </Card>
                            </Grid>
                        </Grid>

                        <MDBox display="flex" justifyContent="center" mb={2}>
                            <MDButton
                                color="info"
                                variant="contained"
                                size="large"
                                disabled={loading || !file1 || !file2}
                                onClick={handleProcessComparison}
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Icon>compare_arrows</Icon>}
                            >
                                {loading ? "Processando e Comparando..." : "Comparar Arquivos"}
                            </MDButton>
                        </MDBox>
                    </Card>
                </Grid>

                {/* RESULTS SUMMARY CARDS & TABLE */}
                {result && (
                    <Grid item xs={12}>
                        <Card
                            variant="outlined"
                            sx={{
                                p: 2,
                                background: darkMode ? ESCURO.cartao : "#ffffff",
                                borderColor: darkMode ? ESCURO.borda : "#e0e0e0"
                            }}
                        >
                            {/* METRIC CARDS */}
                            <Grid container spacing={2} mb={3}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Card variant="outlined" sx={{ p: 2, textAlign: "center", backgroundColor: darkMode ? ESCURO.painel : "#f8f9fa" }}>
                                        <MDTypography variant="caption" sx={{ color: textSecondary }}>Total Arquivo 1 (Base)</MDTypography>
                                        <MDTypography variant="h4" sx={{ color: textPrimary }} fontWeight="bold">{result.totalFile1}</MDTypography>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Card variant="outlined" sx={{ p: 2, textAlign: "center", backgroundColor: darkMode ? ESCURO.painel : "#f8f9fa" }}>
                                        <MDTypography variant="caption" sx={{ color: textSecondary }}>Total Arquivo 2 (Recortes)</MDTypography>
                                        <MDTypography variant="h4" sx={{ color: textPrimary }} fontWeight="bold">{result.totalFile2}</MDTypography>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Card variant="outlined" sx={{ p: 2, textAlign: "center", backgroundColor: darkMode ? "#5a2d32" : "#ffebee", borderColor: "#f44336" }}>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "#ffcdd2" : "#c62828" }}>❌ Não Constam no Arquivo 1</MDTypography>
                                        <MDTypography variant="h4" sx={{ color: darkMode ? "#ffcdd2" : "#c62828" }} fontWeight="bold">{result.missingCount}</MDTypography>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Card variant="outlined" sx={{ p: 2, textAlign: "center", backgroundColor: darkMode ? "#1b4d2e" : "#e8f5e9", borderColor: "#4caf50" }}>
                                        <MDTypography variant="caption" sx={{ color: darkMode ? "#c8e6c9" : "#2e7d32" }}>✅ Constam no Arquivo 1</MDTypography>
                                        <MDTypography variant="h4" sx={{ color: darkMode ? "#c8e6c9" : "#2e7d32" }} fontWeight="bold">{result.foundCount}</MDTypography>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* FILTER TABS & SEARCH */}
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                                <MDBox display="flex" gap={1}>
                                    <Chip
                                        label={`❌ Não Constam no Arq 1 (${result.missingCount})`}
                                        color="error"
                                        variant={filterStatus === "MISSING" ? "filled" : "outlined"}
                                        onClick={() => setFilterStatus("MISSING")}
                                        clickable
                                    />
                                    <Chip
                                        label={`✅ Constam no Arq 1 (${result.foundCount})`}
                                        color="success"
                                        variant={filterStatus === "FOUND" ? "filled" : "outlined"}
                                        onClick={() => setFilterStatus("FOUND")}
                                        clickable
                                    />
                                    <Chip
                                        label={`Todos do Arquivo 2 (${result.totalFile2})`}
                                        color="info"
                                        variant={filterStatus === "ALL" ? "filled" : "outlined"}
                                        onClick={() => setFilterStatus("ALL")}
                                        clickable
                                    />
                                </MDBox>


                                <MDBox width="280px">
                                    <MDInput
                                        placeholder="Filtrar por número ou texto..."
                                        size="small"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        fullWidth
                                    />
                                </MDBox>
                            </MDBox>

                            {/* TABLE */}
                            <TableContainer
                                component={Paper}
                                elevation={0}
                                sx={{
                                    backgroundColor: darkMode ? ESCURO.painel : "#ffffff",
                                    borderRadius: "8px",
                                    border: darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #e0e0e0",
                                    maxHeight: "28rem",
                                    overflowY: "auto"
                                }}
                            >
                                <Table size="small" stickyHeader sx={{ width: "100%", tableLayout: "fixed" }}>
                                    <TableHead sx={{ display: "table-header-group", width: "100%", p: 0 }}>
                                        <TableRow sx={{ display: "table-row", width: "100%" }}>
                                            <TableCell sx={{ display: "table-cell", backgroundColor: darkMode ? `${ESCURO.painelEscuro} !important` : "#f8f9fa !important", color: textPrimary, fontWeight: "bold", width: "12%" }}>
                                                Status
                                            </TableCell>
                                            <TableCell sx={{ display: "table-cell", backgroundColor: darkMode ? `${ESCURO.painelEscuro} !important` : "#f8f9fa !important", color: textPrimary, fontWeight: "bold", width: "25%" }}>
                                                Nº Processo
                                            </TableCell>
                                            <TableCell sx={{ display: "table-cell", backgroundColor: darkMode ? `${ESCURO.painelEscuro} !important` : "#f8f9fa !important", color: textPrimary, fontWeight: "bold", width: "63%" }}>
                                                Detalhes / Intimação (Coluna E / Texto)
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody sx={{ display: "table-row-group", width: "100%" }}>
                                        {displayedRecords().map((rec, idx) => (
                                            <TableRow key={idx} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                                <TableCell sx={{ display: "table-cell", verticalAlign: "top", py: 1.5, px: 2 }}>
                                                    <Chip
                                                        label={rec.status}
                                                        color={rec.status === "NÃO LOCALIZADO" ? "error" : "success"}
                                                        size="small"
                                                        sx={{ fontWeight: "bold", fontSize: "10px" }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ display: "table-cell", verticalAlign: "top", py: 1.5, px: 2, color: textPrimary, fontWeight: "bold" }}>
                                                    {rec.processNumber || "(Sem Processo Identificado)"}
                                                </TableCell>
                                                <TableCell sx={{ display: "table-cell", verticalAlign: "top", py: 1.5, px: 2, color: textSecondary, fontSize: "0.85rem" }}>
                                                    {rec.rawIntimacaoText || rec.cellValues?.join(" | ") || "-"}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {displayedRecords().length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center" sx={{ py: 3, color: textSecondary }}>
                                                    Nenhum registro encontrado para este filtro.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Card>
                    </Grid>
                )}
            </Grid>
        </MDBox>
    );
}
